import { useCallback, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { AppModal, AppRemixIcon } from '@/layout/AppComponents';
import { ProgressBar } from 'react-bootstrap';
import ReactDOMServer from 'react-dom/server';
import * as currentPageAction from '../../../../../store/actions/currentPageAction';
import AuxSidebar from '../../../../Sidebar/AuxSidebar';
import { axiosPath, path } from '../../../../../routing/helpers/router';
import colors from '../../../../../constants/colors';
import Translator from '../../../../components/helpers/Translator';
import useIsMounted from '../../../../components/hooks/useIsMounted';
import _ from 'lodash';

export default function ReportSidebarBlock() {
  const isMounted = useIsMounted();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { moduleBridgeHex, plantHex, filtersHex } = useParams();
  const auxSidebar = useSelector(
    (state) => state.currentPage?.auxSidebar?.opened
  );
  const reportPermissions = useSelector(
    (state) =>
      state.module[moduleBridgeHex]?.permissions?.resourceManager?.reports
  );
  const [openOnce, setOpenOnce] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [reportUrl, setReportUrl] = useState(null);
  const [isError, setIsError] = useState(false);

  const handleAuxSidebar = () => {
    if (!openOnce) {
      setOpenOnce(true);
    }
    if (auxSidebar !== 'reports') {
      dispatch(currentPageAction.change('auxSidebar', { opened: 'reports' }));
    } else {
      dispatch(currentPageAction.change('auxSidebar', { opened: null }));
    }
  };

  // > LOAD REPORT
  const loadReport = useCallback(
    (apiRouteName) => {
      setShowModal(true);
      axios
        .post(axiosPath(apiRouteName), {
          moduleBridgeHex,
          plantHex,
          filtersHex,
          milestoneID: 0,
        })
        .then((r) => {
          if (isMounted) {
            setReportUrl(r.data.reportUrl);
          }
        })
        .catch(() => setIsError(true));
    },
    [moduleBridgeHex, plantHex, filtersHex]
  );
  // < LOAD REPORT

  // < ICON
  const getIconType = useCallback((reportType) => {
    let type = 'file-';
    if (reportType.startsWith('pdf')) {
      type += 'pdf';
    } else if (reportType.startsWith('xls')) {
      type += 'excel';
    } else if (reportType.startsWith('doc')) {
      type += 'word';
    }

    return type;
  }, []);

  const getIconColor = useCallback(
    (reportType) => {
      let color;
      if (reportType.startsWith('pdf')) {
        color = colors.danger;
      } else if (reportType.startsWith('xls')) {
        color = colors.success;
      } else if (reportType.startsWith('doc')) {
        color = colors.primary;
      }

      return color;
    },
    [colors]
  );
  // < ICON

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setReportUrl(null);
    setIsError(false);
  }, []);

  const removeFilters = useCallback(
    () =>
      navigate(
        path({
          name: 'user_module_resourceManager_resource_home',
          params: {
            moduleBridgeHex,
            page: 1,
            filtersHex: 0,
          },
        })
      ),
    [moduleBridgeHex, plantHex]
  );

  return (
    <>
      <li
        className={auxSidebar && auxSidebar === 'reports' ? 'active' : ''}
        onClick={handleAuxSidebar}
      >
        <span className="sidebar-element">
          <AppRemixIcon icon="file-chart" className="sidebar-icon" />{' '}
          <span className="sb-title">
            <Translator file="user" name="user.export.gcl" />
          </span>
        </span>
      </li>
      <AuxSidebar
        title={<Translator file="user" name="user.export.gcl" />}
        isOpen={auxSidebar && auxSidebar === 'reports'}
        width="350px"
        position="left"
        onClose={handleAuxSidebar}
      >
        {openOnce && (
          <>
            {filtersHex != 0 && (
              <div
                className="alert alert-danger alert-dismissible fade show"
                role="alert"
              >
                <Translator
                  file="module-legalCompliance"
                  name="module.lc.export-filtered.selected-filters"
                />
                <button type="button" className="close" onClick={removeFilters}>
                  <AppRemixIcon icon="delete-bin-6" size={16} />
                </button>
              </div>
            )}
            {_.map(reportPermissions, (val, reportType) => {
              if (val && reportType != 0) {
                return (
                  <div
                    key={reportType}
                    className="pointer mb-2"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="download.report"
                      />
                    )}
                    onClick={() =>
                      loadReport(
                        `api_r_user_module_resourceManager_report_${reportType}`
                      )
                    }
                  >
                    <AppRemixIcon
                      icon={getIconType(reportType)}
                      className="sidebar-icon mr-1"
                      color={getIconColor(reportType)}
                    />
                    <Translator
                      file="module-resourceManager"
                      name="resource.asset.inventory"
                    />
                  </div>
                );
              }
            })}
          </>
        )}
      </AuxSidebar>
      <AppModal
        show={showModal}
        title={
          <Translator file="module-legalCompliance" name="export.report" />
        }
        onClose={handleCloseModal}
        isCloseButton={reportUrl || isError}
      >
        {reportUrl ? (
          <div className="text-center">
            <a className="btn btn-sm btn-primary" href={reportUrl}>
              <Translator
                file="module-legalCompliance"
                name="download.report"
              />
            </a>
          </div>
        ) : isError ? (
          <div className="text-danger">
            <Translator
              file="module-legalCompliance"
              name="error.contact.admin"
            />
          </div>
        ) : (
          showModal && (
            <>
              <div>
                <Translator file="module-legalCompliance" name="downloading" />
                ...
              </div>
              <ProgressBar animated now={100} />
            </>
          )
        )}
      </AppModal>
    </>
  );
}
